"use client";

import { Container } from "@/components/ui/container";
import { Button } from "@/components/ui/button";
import { GlowBackdrop } from "@/components/sections/glow-backdrop";
import { useI18n } from "@/i18n/dictionary-context";

// Pixel-art digit grids - each row is a 4-character string where 'X' = filled.
const DIGIT_PATTERNS: Record<"0" | "4", readonly string[]> = {
  "4": ["X..X", "X..X", "X..X", "X..X", "XXXX", "...X", "...X"],
  "0": ["XXXX", "X..X", "X..X", "X..X", "X..X", "X..X", "XXXX"],
} as const;

const DIGIT_WIDTH = 4;
const DIGIT_HEIGHT = 7;
const CELL = 22;
const GAP = 1.5 * CELL;

function PixelDigit({
  digit,
  xOffset,
}: {
  digit: keyof typeof DIGIT_PATTERNS;
  xOffset: number;
}) {
  const rects: React.ReactElement[] = [];
  const pattern = DIGIT_PATTERNS[digit];
  for (let row = 0; row < pattern.length; row++) {
    for (let col = 0; col < pattern[row].length; col++) {
      if (pattern[row][col] === "X") {
        rects.push(
          <rect
            key={`${digit}-${row}-${col}`}
            x={xOffset + col * CELL}
            y={row * CELL}
            width={CELL}
            height={CELL}
            rx={2}
            fill="var(--brand-tint)"
          />,
        );
      }
    }
  }
  return <g>{rects}</g>;
}

function PixelArt404() {
  const digitWidthPx = DIGIT_WIDTH * CELL;
  const totalWidth = digitWidthPx * 3 + GAP * 2;
  const totalHeight = DIGIT_HEIGHT * CELL;
  const positions = [0, digitWidthPx + GAP, (digitWidthPx + GAP) * 2];

  return (
    <svg
      role="img"
      aria-label="404"
      viewBox={`0 0 ${totalWidth} ${totalHeight}`}
      className="w-full max-w-[480px] sm:max-w-[560px]"
    >
      <PixelDigit digit="4" xOffset={positions[0]} />
      <PixelDigit digit="0" xOffset={positions[1]} />
      <PixelDigit digit="4" xOffset={positions[2]} />
    </svg>
  );
}

export default function NotFound() {
  const { dict, locale } = useI18n();
  const home = locale === "en" ? "/" : `/${locale}`;
  return (
    <main className="flex flex-1 flex-col">
      <section className="px-4 py-10 sm:px-10 sm:py-12">
        <Container>
          <div className="relative overflow-hidden rounded-2xl bg-neutral-50/50 px-6 py-20 sm:px-12 sm:py-28">
            <GlowBackdrop />
            <div className="relative flex flex-col items-center gap-10 text-center">
              <PixelArt404 />
              <div className="flex flex-col gap-3">
                <h1 className="text-[36px] font-medium leading-[1.1] tracking-[-0.022em] text-neutral-950 sm:text-[52px] sm:leading-[64px]">
                  {dict.notFound.heading}
                </h1>
                <p className="text-base leading-6 text-neutral-500">
                  {dict.notFound.description}
                </p>
              </div>
              <Button href={home} variant="primary" size="lg" className="rounded-full">
                {dict.notFound.ctaLabel}
              </Button>
            </div>
          </div>
        </Container>
      </section>
    </main>
  );
}
